/**
 * Modal pour ajouter un avenant ou une remise
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TrendingUp, TrendingDown } from 'lucide-react';
import type { AjustementPaiement, AjustementFormData, TypeAjustement } from '@/types/paiement';
import { useModalProtection } from '@/hooks/useModalProtection';


interface AjustementFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: TypeAjustement;
  onSubmit: (data: AjustementFormData) => Promise<AjustementPaiement | null>;
}

export function AjustementFormModal({
  isOpen,
  onClose,
  type,
  onSubmit,
}: AjustementFormModalProps) {
  useModalProtection(isOpen);
  const [montant, setMontant] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAvenant = type === 'avenant';
  const label = isAvenant ? 'avenant' : 'remise';

  // Réinitialiser et fermer
  const handleClose = () => {
    setMontant('');
    setDescription('');
    onClose();
  };

  // Soumettre
  const handleSubmit = async () => {
    const montantNum = parseFloat(montant.replace(/[^0-9.,]/g, '').replace(',', '.'));
    if (isNaN(montantNum) || montantNum <= 0) return;

    setIsSubmitting(true);
    try {
      const result = await onSubmit({
        type,
        montant: montantNum,
        description: description.trim() || undefined,
      });

      if (result) {
        handleClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isAvenant ? (
              <TrendingUp className="h-5 w-5 text-green-500" />
            ) : (
              <TrendingDown className="h-5 w-5 text-red-500" />
            )}
            Ajouter {isAvenant ? 'un avenant' : 'une remise'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Montant */}
          <div className="space-y-2">
            <Label htmlFor="ajust-montant">Montant *</Label>
            <div className="relative">
              <Input
                id="ajust-montant"
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                className="text-right pr-8 text-lg"
                disabled={isSubmitting}
                autoFocus
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                €
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="ajust-description">Description (optionnel)</Label>
            <Textarea
              id="ajust-description"
              placeholder={`Raison de ${isAvenant ? "l'" : 'la '}${label}...`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="resize-none"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !montant}
          >
            {isSubmitting ? 'Ajout...' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AjustementFormModal;
